import { SnackbarProps } from '@mui/material';
import { context } from '@opentelemetry/api';
import { W3CTraceContextPropagator } from '@opentelemetry/core';
import * as Sentry from '@sentry/react';
import { spanToBaggageHeader, spanToTraceHeader } from '@sentry/react';
import { devtoolsExchange } from '@urql/devtools';
import { persistedExchange } from '@urql/exchange-persisted';
import { SubscribePayload } from 'graphql-ws';
import createAuthExchange, { createWebSocketClient } from 'services/auth';
import { createClient, cacheExchange as defaultCacheExchange, fetchExchange, subscriptionExchange } from 'urql';
import { config } from 'config/environments';
import { SnackbarVariant } from 'hooks/useSnackBar';
import { storeTraceID } from 'utils/traceid';

const w3cPropagator = new W3CTraceContextPropagator();

const headersSetter = {
  set(carrier: Headers, key: string, value: string): void {
    carrier.set(key, value);
  },
};

function injectW3CTraceContext(headers: Headers): void {
  w3cPropagator.inject(context.active(), headers, headersSetter);
}

// Custom fetch function that includes Sentry trace propagation (sentry-trace, baggage) and W3C traceparent/tracestate via OpenTelemetry
const createSentryInstrumentedFetch = () => {
  return (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    return Sentry.startSpan(
      {
        name: 'http.client.graphql',
        op: 'http.client',
        attributes: {
          'http.method': init?.method || 'POST',
          'http.url': typeof input === 'string' ? input : input.toString(),
        },
      },
      async (span) => {
        try {
          const sntBaggage = spanToBaggageHeader(span);
          const sntTrace = spanToTraceHeader(span);
          const headers = new Headers(init?.headers);
          if (sntTrace) {
            headers.set('sentry-trace', sntTrace);
          }
          if (sntBaggage) {
            headers.set('baggage', sntBaggage);
          }
          injectW3CTraceContext(headers);

          const response = await fetch(input, {
            ...init,
            headers,
          });
          // Store trace ID from response headers for debugging
          const traceID = response.headers.get('x-cloud-trace-context');
          if (traceID) {
            storeTraceID(traceID);
          }

          // Add response attributes to span
          span?.setAttributes({
            'http.status_code': response.status,
            'http.response.size': response.headers.get('content-length') || 0,
          });

          return response;
        } catch (error) {
          span?.recordException(error as Error);
          throw new Error((error as Error).message);
        }
      },
    );
  };
};

// Create the URQL client
export const makeClient = (showSnackbar?: (props: SnackbarProps, variant?: SnackbarVariant) => void) => {
  const wsClient = createWebSocketClient();

  return createClient({
    url: config.gapiURL,
    requestPolicy: 'cache-and-network',
    exchanges: [
      devtoolsExchange,
      defaultCacheExchange,
      createAuthExchange(showSnackbar),
      persistedExchange({
        preferGetForPersistedQueries: true,
      }),
      subscriptionExchange({
        forwardSubscription(operation) {
          return {
            subscribe: (sink) => {
              const dispose = wsClient.subscribe(operation as SubscribePayload, sink);
              return {
                unsubscribe: dispose,
              };
            },
          };
        },
      }),
      fetchExchange, // The fetchExchange handles the HTTP request, so it should come last
    ],
    fetchOptions: () => {
      return {
        credentials: 'include',
        headers: { Authorization: 'unauthenticated' },
      };
    },
    fetch: createSentryInstrumentedFetch(),
  });
};
